/**
 * Portuguese (Portugal) QWERTY (Windows KBDPO / Microsoft "Portuguese" keyboard, kbdlayout.info
 * KBDPO): maps a physical code to the unshifted character that key emits. The letter rows match US
 * QWERTY; the OEM keys carry Portugal-specific glyphs. The c-cedilla has its own key (the US
 * Semicolon position), the ordinal indicators sit on the US Quote position, and the acute (US
 * BracketRight) and tilde (US Backslash) accents are dead keys, rendered here as their legend glyph
 * only. Backquote is the "\\|" key. The ISO key left of Z ("<>", IntlBackslash) is outside the
 * US-Qwerty code block. Letters uppercase by table convention; the cedilla legend stays lowercase.
 */
export const PORTUGUESE_CODE_TO_CHAR: Record<string, string> = {
  Backquote: "\\", // "\\|" key, VK_OEM_5
  Digit1: "1",
  Digit2: "2",
  Digit3: "3",
  Digit4: "4",
  Digit5: "5",
  Digit6: "6",
  Digit7: "7",
  Digit8: "8",
  Digit9: "9",
  Digit0: "0",
  Minus: "'", // apostrophe (U+0027)
  Equal: "«", // left guillemet (U+00AB)
  KeyQ: "Q",
  KeyW: "W",
  KeyE: "E",
  KeyR: "R",
  KeyT: "T",
  KeyY: "Y",
  KeyU: "U",
  KeyI: "I",
  KeyO: "O",
  KeyP: "P",
  BracketLeft: "+", // plus (U+002B)
  BracketRight: "´", // dead acute (U+00B4)
  Backslash: "~", // dead tilde (U+007E)
  KeyA: "A",
  KeyS: "S",
  KeyD: "D",
  KeyF: "F",
  KeyG: "G",
  KeyH: "H",
  KeyJ: "J",
  KeyK: "K",
  KeyL: "L",
  Semicolon: "ç", // c with cedilla (U+00E7)
  Quote: "º", // masculine ordinal indicator (U+00BA)
  KeyZ: "Z",
  KeyX: "X",
  KeyC: "C",
  KeyV: "V",
  KeyB: "B",
  KeyN: "N",
  KeyM: "M",
  Comma: ",",
  Period: ".",
  Slash: "-", // hyphen-minus (U+002D)
};

/**
 * The .SC2Hotkeys serialization token per code where Portuguese's token diverges from charToken of
 * its character. SC2 binds these OEM keys by virtual-key, so the file names
 * each key by its Portuguese VK rather than the symbol it emits: the apostrophe key serializes as
 * "BracketOpen" (VK_OEM_4), the guillemet key as "BracketClose" (VK_OEM_6), the "+" key as "Equals"
 * (VK_OEM_PLUS), the acute dead key as "SemiColon" (VK_OEM_1), the tilde dead key as "Slash"
 * (VK_OEM_2), the cedilla key as "Grave" (VK_OEM_3), and the ordinal key as "Apostrophe"
 * (VK_OEM_7). Recovered from TheCore's real US-Qwerty -> Portugal pair (both hands) and verified to
 * reproduce those files command-for-command. The Slash key (VK_OEM_MINUS) is omitted: its hyphen
 * legend already serializes to "Minus" via the char path. Letters and digits are positional/char-path
 * and omitted.
 *
 * Backquote -> "Grave" is intentionally NON-injective: it duplicates the cedilla key (Semicolon, also
 * VK_OEM_3 -> "Grave"). TheCore never converts the US backtick key for this locale - every left-hand
 * Backquote binding (Hive/Lair, Lift, ...) is left verbatim on the US "Grave" token (oracle == seed),
 * so convert must emit "Grave" here too to reproduce the file; SC2 reads it as VK_OEM_3 (the cedilla
 * key), TheCore's own collision. Placed last so the inverse parse resolves "Grave" to the cedilla key
 * (first-seen Semicolon), matching SC2; Portugal is therefore excluded from the positional round-trip
 * test, like the ISO "Backslash -> Enter" layouts. Latin America & Spain has the identical quirk. The
 * right-hand file instead relocates the backtick bindings onto Backspace (a hand-specific TheCore
 * quirk), so its only literal "Grave" binding is ChatAllies.
 */
export const PORTUGUESE_CODE_TO_TOKEN: Record<string, string> = {
  Minus: "BracketOpen", // ' apostrophe, VK_OEM_4
  Equal: "BracketClose", // « guillemet, VK_OEM_6
  BracketLeft: "Equals", // +, VK_OEM_PLUS
  BracketRight: "SemiColon", // ´ dead acute, VK_OEM_1
  Backslash: "Slash", // ~ dead tilde, VK_OEM_2
  Semicolon: "Grave", // ç, VK_OEM_3
  Quote: "Apostrophe", // º ordinal, VK_OEM_7
  Backquote: "Grave", // backtick key left unconverted by TheCore; aliases the cedilla key (faithful, non-injective)
};
